import { Controller } from "@hotwired/stimulus"
import { setLabelText } from "../utils/label-text.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  companyLabel: "",
  personLabel: ""
}

/**
 * Entity Type Toggle Controller
 *
 * Handles the visibility of VAT-related fields based on the selected entity type.
 * Physical persons (code 100) are always non-VAT payers, so VAT fields should be hidden.
 */
export default class extends Controller {
  static targets = ["vatSection", "physicalPersonRadio", "legalEntityRadio", "vatPayerType", "nameLabel"]

  static values = {
    translations: { type: Object, default: {} }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.toggle()
  }

  toggle() {
    const isPhysicalPerson = this.hasPhysicalPersonRadioTarget && this.physicalPersonRadioTarget.checked

    if (isPhysicalPerson) {
      // Hide VAT section for physical persons
      this.vatSectionTargets.forEach((section) => {
        section.classList.add("d-none")
      })
      // Clear VAT payer type for physical persons (set to nil/unchecked)
      this.#clearVatPayerType()
      // Clear any open dropdown from legal entity search
      this.#clearLegalEntityDropdown()
      // Update name label
      this.#updateNameLabel(this.#texts.personLabel)
    } else {
      // Show VAT section for legal entities
      this.vatSectionTargets.forEach((section) => {
        section.classList.remove("d-none")
      })
      // Preselect non_payer_vat as default for legal entities
      this.#preselectNonPayerVat()
      // Update name label
      this.#updateNameLabel(this.#texts.companyLabel)
    }
  }

  /**
   * Clears all VAT payer type radio buttons (sets to nil)
   * Physical persons should have vat_payer_type = nil
   */
  #clearVatPayerType() {
    const vatPayerRadios = this.element.querySelectorAll("input[name='party[vat_payer_type]']")
    vatPayerRadios.forEach((radio) => {
      radio.checked = false
    })
  }

  /**
   * Clears the legal entity search dropdown when switching to physical person
   */
  #clearLegalEntityDropdown() {
    const dropdown = this.element.querySelector('[data-legal-entity-target="companyNameList"]')
    if (dropdown) {
      dropdown.innerHTML = ""
      dropdown.classList.remove("show")
    }
  }

  /**
   * Updates the name field label based on entity type.
   *
   * Writes the label's TEXT only: this same label also carries the registry
   * provenance marker (_contact_modal_content.html.erb passes both the
   * "nameLabel" target and the marker into shared/registry/_search). A plain
   * `textContent =` replaces every child, so it destroyed that marker on every
   * modal open — connect() calls toggle() unconditionally — leaving the company
   * name as the one prefilled field with no origin marker, and undercounting
   * the "N údaje" summary banner by one.
   */
  #updateNameLabel(label) {
    if (this.hasNameLabelTarget && label) {
      setLabelText(this.nameLabelTarget, label)
    }
  }

  /**
   * Preselects non_payer_vat radio button when switching to legal entity
   * Only preselects if no VAT payer type is currently selected
   */
  #preselectNonPayerVat() {
    const vatPayerRadios = this.element.querySelectorAll("input[name='party[vat_payer_type]']")
    const anySelected = Array.from(vatPayerRadios).some((radio) => radio.checked)

    if (!anySelected) {
      const nonPayerVatRadio = this.element.querySelector(
        "input[name='party[vat_payer_type]'][value='non_payer_vat']"
      )
      if (nonPayerVatRadio) {
        nonPayerVatRadio.checked = true
      }
    }
  }
}
